import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import Badge from '@atlaskit/badge';
import LinkIcon from '@atlaskit/icon/glyph/link';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { token } from '@atlaskit/tokens';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { AssociatedBillingProfilesProps } from '../../add-edit-payment-method/src/types';
import { CustomPanel } from '../../custom-panel';
import { FormattedAddress } from '../../formatted';
import { getRouteUrl } from '@css-xp/ppc-utils/src/route-utils';
import { routePaths } from '../../route-utils/src/route-paths';

const boxStyles = xcss({
  padding: 'space.050',
  paddingBlockStart: 'space.100',
});

export const AssociatedBillingProfiles = ({
  isLoadingBillingProfiles,
  billingProfiles,
  txaId,
  infoMessage,
  shouldHideDetails = true,
  error,
  shouldHideCountBadge = true,
  isBillingProfileClickable = false,
}: AssociatedBillingProfilesProps) => {
  const getBillingProfileDetailsUrl = (billingProfileId: string) => {
    return getRouteUrl(routePaths.billingProfileDetails, { txaId, billingProfileId });
  };

  if (isLoadingBillingProfiles) {
    return (
      <SkeletonLoaderWrapper testId="associated-billing-profiles-loading">
        <Flex>
          <SkeletonLoader width="10%" marginBottom="0" />
          <SkeletonLoader width="90%" marginBottom="0" />
        </Flex>
      </SkeletonLoaderWrapper>
    );
  }

  if (!billingProfiles?.length && !error) return null;

  const header = (
    <Box paddingInlineStart="space.300">
      {shouldHideCountBadge ? (
        <Text size="small" weight="semibold">
          Linked billing profiles
        </Text>
      ) : (
        <Flex gap="space.100" alignItems="center">
          {/* Above 500 it will show 500+ in badge */}
          <Badge max={500}>{billingProfiles?.length || '-'}</Badge>
          <Text size="small" weight="semibold">
            linked billing profiles
          </Text>
        </Flex>
      )}
    </Box>
  );

  return (
    <CustomPanel
      testId="associated-billing-profiles-container"
      expandedHeader={header}
      collapsedHeader={header}
      version={2}
    >
      <Stack space="space.100" xcss={boxStyles}>
        <Stack
          space="space.100"
          xcss={xcss({
            paddingInline: 'space.100',
            paddingBlockStart: infoMessage ? 'space.200' : 'space.100',
          })}
        >
          {error && (
            <Flex gap="space.100">
              <ErrorIcon label="Failed to load linked billing profiles" primaryColor={token('color.icon.danger')} />
              <Text>{"We couldn't load billing profiles linked to this address. Refresh page to try again."}</Text>
            </Flex>
          )}
          {!error && infoMessage && infoMessage}
          {!error &&
            billingProfiles?.map((billingProfile) => {
              let billingProfileShipToData;
              let addressLine1;

              if (billingProfile?.__typename === 'BillingProfile') {
                billingProfileShipToData = billingProfile.shipTo;
                addressLine1 = billingProfile.shipTo?.address1;
              } else if (billingProfile?.__typename === 'InvoiceGroup') {
                billingProfileShipToData = billingProfile.shipToParty;
                addressLine1 = billingProfile.shipToParty?.addressLine1;
              }

              return (
                <Stack
                  key={billingProfile.id}
                  space="space.100"
                  xcss={xcss({
                    padding: 'space.050',
                  })}
                >
                  {isBillingProfileClickable ? (
                    <Link
                      to={getBillingProfileDetailsUrl(billingProfile.id as string)}
                      data-testid="billing-profile-details-link"
                    >
                      {billingProfile.name}
                    </Link>
                  ) : (
                    <Text testId="billing-profile-name">{billingProfile.name}</Text>
                  )}
                  {!shouldHideDetails && (
                    <>
                      <FormattedAddress
                        addressLine1={addressLine1}
                        city={billingProfileShipToData?.city}
                        country={billingProfileShipToData?.country}
                        postcode={billingProfileShipToData?.postalCode}
                        state={billingProfileShipToData?.state}
                        organizationName={billingProfileShipToData?.name}
                        hasLineBreak={false}
                      />
                      <Flex alignItems="center" gap="space.050">
                        <LinkIcon label="linked states" size="small" primaryColor={token('color.icon.subtle')} />
                        <Text color="color.text.subtlest">{billingProfile?.numberOfCloudsites} linked sites</Text>
                      </Flex>
                      <Flex alignItems="center" gap="space.050">
                        <LinkIcon label="linked states" size="small" primaryColor={token('color.icon.subtle')} />
                        <Text color="color.text.subtlest">
                          {billingProfile?.numberOfSubscriptions} linked subscriptions
                        </Text>
                      </Flex>
                    </>
                  )}
                </Stack>
              );
            })}
        </Stack>
      </Stack>
    </CustomPanel>
  );
};
